import {
  BlogEntityTitleEnum,
  BlogPageEnum,
} from 'modules/automation-rules/enums/automation-rule-enum'
import { AutomationRuleTriggersEnum } from 'modules/automation-rules/triggers/enums/trigger-enum'
import { AutomationRulesTriggersType } from 'modules/automation-rules/triggers/types/automation-rule-trigger-interface'
import { getFunnelStepUrl } from 'modules/funnels/funnel/utils/get-funnel-step-url'
import { ClickLinkTypeEnum } from 'modules/mailing/email-statistics/enums/click-link-type-enum'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { getBlogPostNextjsUrl } from 'shared/utils/get-blog-post-nextjs-url'
import { OpenInNewIcon } from '@systemeio/ui/icons'
import { getUrlWebsiteEdit } from 'modules/websites/utils/get-url'

export const TriggerListContent = ({ trigger }: { trigger: AutomationRulesTriggersType }) => {
  const { t } = useLocoTranslation()

  switch (trigger.type) {
    case AutomationRuleTriggersEnum.blog_page_form_subscribed: {
      return (
        <div className={'flex'}>
          <div className="truncate">
            <LinkWithoutPrefetch
              className="primary-link"
              href={getBlogPostNextjsUrl(trigger.blog.id)}
            >
              {trigger.blog.name}
            </LinkWithoutPrefetch>
          </div>
          {`:`}
          <div className={'ml-1'}>
            {(trigger.page.type === BlogPageEnum.blog_layout ||
              trigger.page.type === BlogPageEnum.blog_post_layout) && (
              <span>{t(BlogEntityTitleEnum[trigger.page.type])}</span>
            )}
            {(trigger.page.type === BlogPageEnum.blog_home ||
              trigger.page.type === BlogPageEnum.blog_static ||
              trigger.page.type === BlogPageEnum.blog_post_listing ||
              trigger.page.type === BlogPageEnum.blog_search) &&
              trigger.page.blogPage && <span>{trigger.page.blogPage.name}</span>}
            {trigger.page.type === BlogPageEnum.blog_post_body && (
              <span>{trigger.page.blogPost.name}</span>
            )}
          </div>
        </div>
      )
    }
    case AutomationRuleTriggersEnum.form_subscribed:
    case AutomationRuleTriggersEnum.new_sale:
    case AutomationRuleTriggersEnum.sale_canceled:
    case AutomationRuleTriggersEnum.page_visited:
    case AutomationRuleTriggersEnum.subscription_payment_failed: {
      return (
        <div className={'flex items-center gap-2'}>
          <span
            className={'whitespace-normal'}
          >{`${trigger.funnelStep.funnel?.name}: ${trigger.funnelStep.name}`}</span>
          <LinkWithoutPrefetch
            href={getFunnelStepUrl(trigger.funnelStep.funnel.id, trigger.funnelStep.id)}
            rel="noreferrer"
            target={'_self'}
            className={`group outline-hidden`}
          >
            <OpenInNewIcon
              className={
                'main-transition-colors group-focus-visible:fill-brand-primary hover:fill-brand-primary'
              }
            />
          </LinkWithoutPrefetch>
        </div>
      )
    }
    case AutomationRuleTriggersEnum.registered_to_webinar: {
      return trigger.funnelStep ? (
        <div className={'flex gap-2'}>
          <span>{`${trigger.funnelStep.funnel?.name}: ${trigger.funnelStep.name}`}</span>
          <LinkWithoutPrefetch
            href={getFunnelStepUrl(trigger.funnelStep.funnel.id, trigger.funnelStep.id)}
            rel="noreferrer"
            target={'_self'}
            className={`group outline-hidden`}
          >
            <OpenInNewIcon
              className={
                'main-transition-colors group-focus-visible:fill-brand-primary hover:fill-brand-primary'
              }
            />
          </LinkWithoutPrefetch>
        </div>
      ) : (
        <span>{trigger.webinar.name}</span>
      )
    }
    case AutomationRuleTriggersEnum.tag_added:
    case AutomationRuleTriggersEnum.tag_removed: {
      return <span>{trigger.tag.name}</span>
    }
    case AutomationRuleTriggersEnum.email_link_clicked:
      return (
        <div className={'flex flex-col gap-1'}>
          <span className="truncate">{trigger.mailing.currentEmail.subject}</span>
          {trigger.link && (
            <div className={'flex gap-1 pr-0.5'}>
              <LinkWithoutPrefetch
                className="primary-link truncate"
                href={trigger.link.originalUrl}
              >
                {trigger.link.displayLinkName}
              </LinkWithoutPrefetch>
              {trigger.link.type === ClickLinkTypeEnum.AttachedFile && (
                <i>{t('dashboard.email_statistics.table_links.link_type.attachment_title')}</i>
              )}
            </div>
          )}
        </div>
      )
    case AutomationRuleTriggersEnum.email_opened: {
      return <span>{trigger.mailing.currentEmail.subject}</span>
    }
    case AutomationRuleTriggersEnum.campaign_completed: {
      return <span>{trigger.campaign.name}</span>
    }
    case AutomationRuleTriggersEnum.enrolled_in_course:
    case AutomationRuleTriggersEnum.course_completed: {
      return <span>{trigger.course.name}</span>
    }
    case AutomationRuleTriggersEnum.module_completed: {
      return (
        <div className={'flex flex-col gap-1'}>
          <div className={'flex gap-1'}>
            {`${t('dashboard.courses.title')}:`}
            <span>{trigger.course.name}</span>
          </div>
          {trigger.module && (
            <div className={'flex gap-1'}>
              {`${t('dashboard.course.module_title')}:`}
              <span>{trigger.module.name}</span>
            </div>
          )}
        </div>
      )
    }
    case AutomationRuleTriggersEnum.lecture_completed: {
      return (
        <div className={'flex flex-col gap-1'}>
          <div className={'flex gap-1'}>
            {`${t('dashboard.courses.title')}:`}
            <span>{trigger.course.name}</span>
          </div>
          {trigger.module && (
            <div className={'flex gap-1'}>
              {`${t('dashboard.course.module_title')}:`}
              <span>{trigger.module.name}</span>
            </div>
          )}
          {trigger.lecture && (
            <div className={'flex gap-1'}>
              {`${t('dashboard.course.lecture_title')}:`}
              <span>{trigger.lecture.name}</span>
            </div>
          )}
        </div>
      )
    }
    case AutomationRuleTriggersEnum.enrolled_in_community: {
      // TODO remove ? after backend fix
      return <span>{trigger.community?.name}</span>
    }
    case AutomationRuleTriggersEnum.enrolled_in_course_bundle: {
      return <span>{trigger.courseBundle.name}</span>
    }
    case AutomationRuleTriggersEnum.digital_store_form_subscribed: {
      return (
        <div className={'flex flex-col gap-1'}>
          <div className={'flex gap-1'}>
            {`${t('dashboard.store.title')}:`}
            <span>{trigger.digitalStore.name}</span>
            <LinkWithoutPrefetch
              href={`/stores/${trigger.digitalStore.id}`}
              rel="noreferrer"
              target={'_self'}
              className={`group outline-hidden`}
            >
              <OpenInNewIcon
                className={
                  'main-transition-colors group-focus-visible:fill-brand-primary hover:fill-brand-primary'
                }
              />
            </LinkWithoutPrefetch>
          </div>
          <div className={'flex gap-1'}>
            {`${t('dashboard.automation_rule.store_optin')}:`}
            <span>{trigger.itemOptIn.title}</span>
          </div>
        </div>
      )
    }
    case AutomationRuleTriggersEnum.booking_created: {
      return (
        <div className={'flex gap-1'}>
          {`${t('dashboard.automation_rule.event_title')}:`}
          <span>{trigger.bookingEvent.name}</span>
          <LinkWithoutPrefetch
            href={`/calendar/${trigger.bookingEvent.id}`}
            rel="noreferrer"
            target={'_self'}
            className={`group outline-hidden`}
          >
            <OpenInNewIcon
              className={
                'main-transition-colors group-focus-visible:fill-brand-primary hover:fill-brand-primary'
              }
            />
          </LinkWithoutPrefetch>
        </div>
      )
    }
    case AutomationRuleTriggersEnum.website_form_subscribed: {
      return (
        <div className={'flex items-center gap-2'}>
          <span
            className={'whitespace-normal'}
          >{`${trigger.website.name}: ${trigger.website.websitePage.name}`}</span>
          <LinkWithoutPrefetch
            href={getUrlWebsiteEdit(trigger.website.websitePage.pageContentId) ?? ''}
            rel="noreferrer"
            target={'_self'}
            className={`group outline-hidden`}
          >
            <OpenInNewIcon
              className={
                'main-transition-colors group-focus-visible:fill-brand-primary hover:fill-brand-primary'
              }
            />
          </LinkWithoutPrefetch>
        </div>
      )
    }
  }
}
